export const normalizeText = (value = "") =>
  String(value || "")
    .trim()
    .toLowerCase();

export const upperText = (value = "") =>
  String(value || "")
    .trim()
    .toUpperCase();

const toMillis = (value) => {
  if (!value) return 0;
  if (typeof value?.toMillis === "function") return value.toMillis();
  if (Number.isFinite(value?.seconds)) return value.seconds * 1000;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

export const isRemovedRecord = (record = {}) =>
  record.isDeleted === true ||
  record.deleted === true ||
  record.isArchived === true ||
  record.archived === true ||
  Boolean(record.deletedAt || record.archivedAt || record.removedAt) ||
  [
    record.status,
    record.registrationStatus,
    record.allocationStatus,
    record.recordStatus,
  ].some((value) =>
    ["DELETED", "ARCHIVED", "REMOVED", "TRASHED"].includes(upperText(value)),
  );

export const recordSupportsState = (record = {}, state = "") => {
  const target = normalizeText(state);
  if (!target) return true;
  const direct = normalizeText(
    record.state || record.stateName || record.projectState || record.pacsState,
  );
  const states = [
    ...(Array.isArray(record.serviceStates) ? record.serviceStates : []),
    ...(Array.isArray(record.states) ? record.states : []),
    ...(Array.isArray(record.allowedStates) ? record.allowedStates : []),
  ].map(normalizeText);
  return !direct && !states.length ? false : direct === target || states.includes(target);
};

export const pacsDisplayName = (record = {}) =>
  record.societyName ||
  record.organizationName ||
  record.pacsName ||
  record.cooperativeName ||
  record.applicantName ||
  record.name ||
  record.email ||
  "PACS / Society";

export const pmcDisplayName = (record = {}) =>
  record.organizationName ||
  record.agencyName ||
  record.companyName ||
  record.pmcName ||
  record.name ||
  record.email ||
  "PMC Agency";

export const canonicalPacsUid = (record = {}) =>
  record.canonicalPacsUid ||
  record.uid ||
  record.pacsUid ||
  record.pacsUserUid ||
  record.pacsUserId ||
  record.pacsId ||
  record.id ||
  "";

export const canonicalPmcUid = (record = {}) =>
  record.canonicalPmcUid ||
  record.uid ||
  record.pmcUid ||
  record.pmcId ||
  record.id ||
  "";

export const allocationPmcUid = (record = {}) =>
  record.allocatedPmcUid ||
  record.allocatedPmcId ||
  record.assignedPmcUid ||
  record.assignedPmcId ||
  record.pmcUid ||
  record.pmcId ||
  "";

export const allocationPmcName = (record = {}) =>
  record.allocatedPmcName ||
  record.assignedPmcName ||
  record.pmcName ||
  record.pmcAgencyName ||
  "";

export const canonicalPmcNameKey = (value = "") => {
  const key = normalizeText(value).replace(/[^a-z0-9]+/g, " ").trim();
  if (
    key.includes("sun shine overseas") ||
    key.includes("sunshine overseas") ||
    (key.includes("shine agri allied") && key.includes("marketing"))
  ) {
    return "shine-agri-allied-current-profile";
  }
  return key;
};

export const resolvePmcReference = (reference = {}, pmcs = []) => {
  const referenceIds = [
    allocationPmcUid(reference),
    reference.allocatedPmcId,
    reference.assignedPmcUid,
    reference.assignedPmcId,
    reference.pmcUid,
    reference.pmcId,
  ]
    .map(normalizeText)
    .filter(Boolean);
  const identityMatch = pmcs.find((pmc) =>
    [canonicalPmcUid(pmc), pmc.id, pmc.uid, pmc.email, pmc.officialEmail]
      .map(normalizeText)
      .filter(Boolean)
      .some((value) => referenceIds.includes(value)),
  );
  if (identityMatch) return identityMatch;
  const nameKey = canonicalPmcNameKey(allocationPmcName(reference));
  return nameKey
    ? pmcs.find(
        (pmc) => canonicalPmcNameKey(pmcDisplayName(pmc)) === nameKey,
      ) || null
    : null;
};

export const pacsIdentityKeys = (record = {}) => {
  const registration = normalizeText(
    record.pacsRegistrationNo ||
      record.pacsRegistrationNumber ||
      record.registrationNumber ||
      record.registrationNo,
  );
  const email = normalizeText(record.pacsEmail || record.email || record.officialEmail);
  const mobile = normalizeText(record.pacsMobile || record.mobile || record.phone).replace(
    /\D/g,
    "",
  );
  const name = normalizeText(pacsDisplayName(record));
  const location = normalizeText(
    `${record.state || record.stateName || ""}|${record.district || record.districtName || ""}`,
  );
  return [
    record.id && `uid:${normalizeText(record.id)}`,
    record.uid && `uid:${normalizeText(record.uid)}`,
    record.pacsUid && `uid:${normalizeText(record.pacsUid)}`,
    record.pacsUserUid && `uid:${normalizeText(record.pacsUserUid)}`,
    record.pacsUserId && `uid:${normalizeText(record.pacsUserId)}`,
    record.pacsId && `uid:${normalizeText(record.pacsId)}`,
    record.societyUid && `uid:${normalizeText(record.societyUid)}`,
    registration && `reg:${registration}`,
    email && `email:${email}`,
    mobile.length >= 8 && `mobile:${mobile}`,
    name && `name-location:${name}|${location}`,
  ].filter(Boolean);
};

const pmcIdentityKeys = (record = {}) => {
  const registration = normalizeText(
    record.registrationNumber ||
      record.registrationNo ||
      record.pmcRegistrationNo ||
      record.gstin ||
      record.cin,
  );
  const email = normalizeText(record.email || record.officialEmail);
  const mobile = normalizeText(record.mobile || record.phone).replace(/\D/g, "");
  const name = normalizeText(pmcDisplayName(record));
  return [
    canonicalPmcUid(record) && `uid:${normalizeText(canonicalPmcUid(record))}`,
    registration && `reg:${registration}`,
    email && `email:${email}`,
    mobile.length >= 8 && `mobile:${mobile}`,
    name && `name:${name}`,
  ].filter(Boolean);
};

const mergeDefined = (primary = {}, secondary = {}) => {
  const merged = { ...secondary, ...primary };
  Object.keys(merged).forEach((field) => {
    if (primary[field] === undefined || primary[field] === null || primary[field] === "") {
      merged[field] = secondary[field];
    }
  });
  return merged;
};

const dedupe = (records, keyBuilder, uidBuilder, sourceField) => {
  const groups = new Map();
  const aliases = new Map();
  records
    .filter((record) => !isRemovedRecord(record))
    .slice()
    .sort(
      (left, right) =>
        toMillis(left.updatedAt || left.approvedAt || left.createdAt) -
        toMillis(right.updatedAt || right.approvedAt || right.createdAt),
    )
    .forEach((record) => {
      const keys = keyBuilder(record);
      const existing = keys.map((key) => aliases.get(key)).find(Boolean);
      const groupId = existing || uidBuilder(record) || keys[0];
      if (!groupId) return;
      const current = groups.get(groupId);
      const merged = current ? mergeDefined(record, current) : record;
      const sourceIds = Array.from(
        new Set(
          [
            ...(current?.[sourceField] || []),
            record.id || record.uid || uidBuilder(record),
          ].filter(Boolean),
        ),
      );
      groups.set(groupId, {
        ...merged,
        [sourceField]: sourceIds,
        duplicateCount: Math.max(sourceIds.length - 1, 0),
      });
      keys.forEach((key) => aliases.set(key, groupId));
    });
  return Array.from(groups.values());
};

export const dedupePacs = (records = []) =>
  dedupe(records, pacsIdentityKeys, canonicalPacsUid, "sourceIds").map((record) => ({
    ...record,
    canonicalPacsUid: canonicalPacsUid(record),
    canonicalApprovalStatus:
      record.status ||
      record.registrationStatus ||
      record.roleApprovalStatus ||
      record.branchApprovalStatus ||
      "",
    canonicalIsActive: record.isActive,
  }));

export const dedupePmcs = (records = []) =>
  dedupe(records, pmcIdentityKeys, canonicalPmcUid, "sourceIds").map((record) => ({
    ...record,
    canonicalPmcUid: canonicalPmcUid(record),
  }));

export const isApprovedPmc = (record = {}) =>
  !isRemovedRecord(record) &&
  record.isActive !== false &&
  [
    record.status,
    record.registrationStatus,
    record.roleApprovalStatus,
    record.branchApprovalStatus,
    record.approvalStatus,
  ].some((value) => upperText(value) === "APPROVED");

export const isActiveAllocation = (record = {}) => {
  if (!record || isRemovedRecord(record) || record.isActive === false) return false;
  const status = upperText(record.allocationStatus || record.status || "ACTIVE");
  return (
    ![
      "UNALLOCATED",
      "INACTIVE",
      "REVOKED",
      "SUPERSEDED",
      "EXPIRED",
      "DELETED",
      "ARCHIVED",
      "REMOVED",
    ].includes(status) && Boolean(allocationPmcUid(record) || allocationPmcName(record))
  );
};

export const currentAllocationMap = (allocations = []) => {
  const map = new Map();
  allocations
    .filter((record) => !isRemovedRecord(record) || upperText(record.status) === "UNALLOCATED")
    .slice()
    .sort(
      (left, right) =>
        toMillis(left.updatedAt || left.allocatedAt || left.createdAt) -
        toMillis(right.updatedAt || right.allocatedAt || right.createdAt),
    )
    .forEach((allocation) => {
      pacsIdentityKeys(allocation).forEach((key) => map.set(key, allocation));
    });
  return map;
};
